type CaptureEventFn = (event: {
  message: string
  level: 'error' | 'warning'
  extra: Record<string, unknown>
}) => void

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return (input as Request).url
}

// A refusal or a missing row is the Worker answering, not failing. 401 is an
// expired session, which the apps handle by sending the user to sign in.
const EXPECTED_STATUSES = new Set([401, 403, 404])

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

// The route's shape rather than the exact path: Sentry groups these events by
// their message, so an id in it would open a new issue for every gym or item.
function routeOf(url: string, apiUrl: string): string {
  const path = url.replace(apiUrl, '').split(/[?#]/)[0].replace(UUID, ':id')
  return path || '/'
}

export function setupSentryFetch(apiUrl: string, captureEvent: CaptureEventFn): void {
  const _fetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const res = await _fetch(input, init)
    const url = getUrl(input)
    if (!res.ok && !EXPECTED_STATUSES.has(res.status) && url.startsWith(apiUrl)) {
      const body = await res
        .clone()
        .json()
        .catch(() => ({}))
      captureEvent({
        message: `${init?.method ?? 'GET'} ${routeOf(url, apiUrl)} failed`,
        level: res.status >= 500 ? 'error' : 'warning',
        extra: {
          status: res.status,
          error: (body as { error?: string }).error ?? 'unknown error',
          url,
          method: init?.method ?? 'GET',
        },
      })
    }
    return res
  }
}
